import React from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const TutorialPage = ({ onStart }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const handleStart = () => {
    if (onStart) {
      onStart()
    }
  }

  const steps = [
    {
      emoji: '✨',
      title: t('roadmap.tutorialTitle1'),
      description: t('roadmap.tutorialBody1'),
    },
    {
      emoji: '🧭',
      title: t('roadmap.tutorialTitle2'),
      description: t('roadmap.tutorialBody2'),
    },
    {
      emoji: '🏗️',
      title: t('roadmap.tutorialTitle3'),
      description: t('roadmap.tutorialBody3'),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundImage: 'url(/small.png)',
        backgroundSize: '130% auto',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Overlay for entire page */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 29, 0.3)',
          zIndex: 0,
        }}
      />

      {/* Logo */}
      <Box
        sx={{
          position: 'absolute',
          top: 32,
          left: 32,
          zIndex: 2,
        }}
      >
      </Box>

      {/* Header Section - Top Half */}
      <Box
        sx={{
          flex: '1 1 50%',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: 3,
          py: 8,
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: '900px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
              lineHeight: 1.2,
              textShadow: '3px 3px 6px rgba(0, 0, 0, 0.9), -2px -2px 4px rgba(0, 0, 0, 0.9), 2px 2px 4px rgba(0, 0, 0, 0.9), -2px 2px 4px rgba(0, 0, 0, 0.9), 2px -2px 4px rgba(0, 0, 0, 0.9)',
            }}
          >
            {t('roadmap.tutorialMainTitle')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              mb: 1,
              fontWeight: 400,
              fontSize: { xs: '1.2rem', md: '1.6rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8)',
            }}
          >
            {t('roadmap.tutorialSubTitle')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(150, 150, 150, 0.95)',

              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1,
              marginBottom: 7,
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8)',
            }}
          >
            {t('roadmap.tutorialHeading')}
          </Typography>
        </Box>
      </Box>

      {/* Tutorial Section - Bottom Half with Glassmorphism */}
      <Box
        sx={{
          flex: '1 1 50%',
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(15, 15, 25, 0.85)',
          backdropFilter: 'blur(30px)',
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px',
          py: { xs: 5, md: 7 },
          px: { xs: 3, md: 4 },
          mt: { xs: -4, md: -6 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              mb: 5,
              textAlign: 'start',
              fontWeight: 500,
              fontSize: { xs: '1.4rem', md: '1.6rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8)',
            }}
          >
            {t('roadmap.tutorialMiniTitle')}
          </Typography>

          {/* Three Tutorial Columns */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 5 },
              mb: 6,
            }}
          >
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      mb: 2,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {step.emoji}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      mb: 2,
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8)',
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.85)',
                      lineHeight: 1.75,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      textShadow: '1px 1px 3px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px -1px 2px rgba(0, 0, 0, 0.8), -1px 1px 2px rgba(0, 0, 0, 0.8)',
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
                {index < steps.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      display: { xs: 'none', md: 'block' },
                      mx: 2,
                      alignSelf: 'stretch',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* CTA Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleStart}
              sx={{
                px: { xs: 10, md: 10 },
                py: { xs: 0.5, md: 0.5 },
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                backgroundColor: '#1976d2',
                borderRadius: '6px',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#0d47a1',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {t('roadmap.startRoadmap')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default TutorialPage

